/**
 * Device housekeeping (ADR-0021, ADR-0028): a survey's answers and photos
 * leave the phone once the server holds all of them and the reporter can no
 * longer change them — never earlier.
 */

/** How long a submitted, fully synced survey stays readable on the device. */
export const SYNCED_SURVEY_GRACE_MS = 7 * 86_400_000;

/** Statuses in which the reporter may still edit on the device. */
const EDITABLE = new Set(['DRAFT', 'RETURNED']);

export interface LocalSurveyState {
  id: string;
  status: string;
  syncState: 'local' | 'pending' | 'synced' | 'rejected';
  /** Last local change (submission time for a submitted survey). */
  updatedAt: number;
  /** Cancelled or reassigned on the server: the reporter decides when to remove it. */
  withdrawn?: boolean;
  /** Outbox items still waiting for this survey. */
  queued: number;
  /** Attachments stored on the device that have not reached the server. */
  unsentAttachments: number;
}

/**
 * Surveys that can be removed from the device: past RETURNED (submitted
 * onwards), synced with nothing left in the outbox, every attachment
 * uploaded, and untouched for the grace period. A survey the analyst returns
 * later comes back with the next offline pack (photos are then read from
 * the server).
 */
export function purgeableSurveys(surveys: LocalSurveyState[], nowMs: number, graceMs = SYNCED_SURVEY_GRACE_MS): string[] {
  return surveys
    .filter(
      (s) =>
        !EDITABLE.has(s.status) &&
        s.status !== 'CANCELLED' &&
        !s.withdrawn &&
        s.syncState === 'synced' &&
        s.queued === 0 &&
        s.unsentAttachments === 0 &&
        nowMs - s.updatedAt >= graceMs,
    )
    .map((s) => s.id);
}
